import type { TailRiskBlock } from '../lib/types';
import { clamp } from '../lib/format';
import { InfoPopover } from './InfoPopover';
import styles from './TailRiskMeter.module.css';

interface Props {
  block: TailRiskBlock;
  className?: string;
}

const tierColor = {
  HIGH: 'var(--tia-negative)',
  ELEVATED: 'var(--tia-warning)',
  MODERATE: 'var(--tia-info)',
  LOW: 'var(--tia-positive)',
};

export function TailRiskMeter({ block, className }: Props) {
  const overallPct = clamp(block.overallScore, 0, 1) * 100;
  // weightedExposure is signed -1..1; map to 0..100
  const exposurePct = ((clamp(block.weightedExposure, -1, 1) + 1) / 2) * 100;
  const tierTone = tierColor[block.qualifier];

  return (
    <div className={[styles.wrap, className].filter(Boolean).join(' ')}>
      <div className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={styles.labelRow}>
            <span className={styles.label}>Tail Risk Magnitude</span>
            <InfoPopover
              label="What is tail-risk magnitude?"
              content={
                <span>
                  Total density of probability-laden language ("could / may / might / possibly") normalized to 0–100.
                  <strong> Higher = more hedged language</strong>; not directionally good or bad on its own.
                </span>
              }
            />
          </span>
          <span className={[styles.value, 'mono'].join(' ')} style={{ color: tierTone }}>
            {(block.overallScore * 100).toFixed(0)}
          </span>
          <span className={styles.qualifier} style={{ color: tierTone }}>{block.qualifier}</span>
          <div className={styles.bar}>
            <div className={styles.barFill} style={{ width: `${overallPct}%`, background: tierTone }} />
          </div>
        </div>

        <div className={styles.summaryItem}>
          <span className={styles.labelRow}>
            <span className={styles.label}>Weighted Exposure</span>
            <InfoPopover
              label="What is weighted exposure?"
              content={
                <span>
                  Same hedge language, but weighted by topical context (guidance / cost / litigation, etc.) and
                  signed: <strong>negative = downside-skewed</strong>, <strong>positive = upside-skewed</strong>,
                  near zero = balanced. Tells you whether the hedging is mostly about good things or bad things.
                </span>
              }
            />
          </span>
          <span className={[styles.value, 'mono'].join(' ')}>
            {block.weightedExposure > 0 ? '+' : ''}{block.weightedExposure.toFixed(2)}
          </span>
          <span className={styles.qualifier}>
            {block.weightedExposure > 0.05 ? 'Upside-skewed' : block.weightedExposure < -0.05 ? 'Downside-skewed' : 'Balanced'}
          </span>
          <div className={styles.exposureWrap}>
            <div className={styles.exposureTrack}>
              <div className={styles.exposureCenter} aria-hidden="true" />
              <div className={styles.exposureMarker} style={{ left: `${exposurePct}%` }} />
            </div>
            <div className={styles.exposureLabels}>
              <span>Downside</span>
              <span>Balanced</span>
              <span>Upside</span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.split}>
        <section>
          <h4 className={styles.subheading}>Term frequencies</h4>
          <ul className={styles.termList}>
            {block.terms.map((t) => {
              const probPct = clamp(t.impliedProbability, 0, 1) * 100;
              return (
                <li key={t.term} className={styles.termRow}>
                  <span className={styles.termName}>{t.term}</span>
                  <span className={styles.termCtx}>{t.context}</span>
                  <span className={[styles.termCount, 'mono'].join(' ')}>×{t.count}</span>
                  <div className={styles.probBar}>
                    <div className={styles.probFill} style={{ width: `${probPct}%` }} />
                  </div>
                  <span className={[styles.termProb, 'mono'].join(' ')}>{probPct.toFixed(0)}%</span>
                </li>
              );
            })}
          </ul>
        </section>

        <section>
          <h4 className={styles.subheading}>Top quotes</h4>
          <ul className={styles.quoteList}>
            {block.topQuotes.map((q, i) => (
              <li key={i} className={styles.quoteItem}>
                <blockquote className={styles.quote}>“{q.quote}”</blockquote>
                <span className={styles.quoteMeta}>
                  <span className={styles.quoteTerm}>{q.term}</span>
                  <span className={[styles.quoteRef, 'mono'].join(' ')}>{q.ref}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
